// src/routes/dashboard/integrations.tsx
//
// Org-facing HRIS/payroll integrations screen (employer roles).
// Lists this org's connections, provides a "Connect provider" wizard driven by
// the provider's connection_config_schema, and per-connection sync/delete actions.

import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { BRAND_MINT } from '@/lib/utils'
import {
  listActiveProvidersApi,
  listOrgConnectionsApi,
  createOrgConnectionApi,
  testConnectionApi,
  deleteOrgConnectionApi,
  triggerSyncApi,
  listSyncLogsApi,
  listPendingChangesApi,
  approvePendingChangeApi,
  rejectPendingChangeApi,
  type IntegrationProvider,
  type IntegrationConnection,
  type IntegrationFieldDef,
  type SyncLogEntry,
  type PendingChange,
} from '@/lib/integrations'
import {
  LoaderIcon,
  PlusIcon,
  Trash2Icon,
  RefreshCwIcon,
  PlugIcon,
  FileTextIcon,
  ShieldAlertIcon,
  CheckIcon,
  XIcon,
} from 'lucide-react'

function statusClass(status: string | null | undefined): string {
  switch (status) {
    case 'active':
    case 'success':
    case 'completed':
      return 'bg-emerald-500/10 text-emerald-600'
    case 'error':
    case 'failed':
      return 'bg-red-500/10 text-red-600'
    case 'partial':
      return 'bg-amber-500/10 text-amber-600'
    case 'paused':
    case 'disconnected':
      return 'bg-slate-500/10 text-slate-500'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function fieldInputType(field: IntegrationFieldDef): string {
  if (field.secret) return 'password'
  if (field.type === 'integer' || field.type === 'number') return 'number'
  if (field.type === 'uri' || field.format === 'uri') return 'url'
  if (field.type === 'boolean') return 'checkbox'
  return 'text'
}

function changeDescription(c: PendingChange): string {
  switch (c.change_type) {
    case 'terminate': return 'Deactivate employee (no longer in the HRIS roster)'
    case 'bank_update': return 'Update bank account details'
    case 'field_update': return 'Update employee fields'
    default: return c.change_type
  }
}

function SyncLogsDialog({ connectionId, onClose }: { connectionId: number; onClose: () => void }) {
  const [logs, setLogs] = useState<SyncLogEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    listSyncLogsApi(connectionId)
      .then((r) => { if (active) setLogs(r.sync_logs) })
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load sync history'))
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [connectionId])

  return (
    <Dialog open onClose={onClose} title="Sync history" description="Recent sync runs for this connection.">
      <div className="max-h-96 overflow-y-auto py-2">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : logs.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No sync runs yet.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <div key={log.log_id} className="rounded-lg border border-border bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{log.sync_type}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(log.status)}`}>{log.status}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {log.records_total != null && <span>{log.records_total} records</span>}
                  {log.records_imported != null && <span> · {log.records_imported} imported</span>}
                  {log.records_failed != null && <span> · {log.records_failed} failed</span>}
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground/60">
                  {new Date(log.started_at).toLocaleString()}
                  {log.completed_at && ` → ${new Date(log.completed_at).toLocaleString()}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}

function PendingChangesDialog({ connectionId, onClose }: { connectionId: number; onClose: () => void }) {
  const [changes, setChanges] = useState<PendingChange[]>([])
  const [loading, setLoading] = useState(true)
  const [actingOn, setActingOn] = useState<number | null>(null)

  const load = () => {
    setLoading(true)
    listPendingChangesApi(connectionId)
      .then((r) => setChanges(r.pending_changes))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load pending changes'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [connectionId])

  const review = async (changeId: number, action: 'approve' | 'reject') => {
    setActingOn(changeId)
    try {
      if (action === 'approve') await approvePendingChangeApi(connectionId, changeId)
      else await rejectPendingChangeApi(connectionId, changeId)
      toast.success(action === 'approve' ? 'Change approved.' : 'Change rejected.')
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${action} change`)
    } finally {
      setActingOn(null)
    }
  }

  return (
    <Dialog open onClose={onClose} title="Review import changes" description="Approve or reject changes staged by the HRIS sync (review mode).">
      <div className="max-h-96 overflow-y-auto py-2">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : changes.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No pending changes to review.</p>
        ) : (
          <div className="space-y-2">
            {changes.map((c) => (
              <div key={c.change_id} className="rounded-lg border border-border bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {c.first_name || ''} {c.last_name || ''}
                      {c.email && <span className="text-xs text-muted-foreground"> · {c.email}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{changeDescription(c)}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="outline" onClick={() => review(c.change_id, 'approve')} disabled={actingOn === c.change_id} className="gap-1">
                      <CheckIcon className="size-3.5" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => review(c.change_id, 'reject')} disabled={actingOn === c.change_id} className="gap-1 text-destructive">
                      <XIcon className="size-3.5" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}

export const Route = createFileRoute('/dashboard/integrations')({
  component: () => {
    const [providers, setProviders] = useState<IntegrationProvider[]>([])
    const [connections, setConnections] = useState<IntegrationConnection[]>([])
    const [loading, setLoading] = useState(true)

    // Connect wizard state
    const [showConnect, setShowConnect] = useState(false)
    const [selectedProviderId, setSelectedProviderId] = useState<string>('')
    const [config, setConfig] = useState<Record<string, string>>({})
    const [syncEmployees, setSyncEmployees] = useState(true)
    const [syncPayroll, setSyncPayroll] = useState(false)
    const [enableWriteback, setEnableWriteback] = useState(false)
    const [testing, setTesting] = useState(false)
    const [connecting, setConnecting] = useState(false)

    const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
    const [syncingId, setSyncingId] = useState<number | null>(null)
    const [logsConnectionId, setLogsConnectionId] = useState<number | null>(null)
    const [reviewConnectionId, setReviewConnectionId] = useState<number | null>(null)

    const selectedProvider = providers.find((p) => p.provider_id === Number(selectedProviderId))

    const load = async () => {
      try {
        const [p, c] = await Promise.all([listActiveProvidersApi(), listOrgConnectionsApi()])
        setProviders(p.providers)
        setConnections(c.connections)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load integrations')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => { load() }, [])

    const selectProvider = (id: string) => {
      setSelectedProviderId(id)
      const provider = providers.find((p) => p.provider_id === Number(id))
      const defaults: Record<string, string> = {}
      for (const f of provider?.connection_config_schema ?? []) {
        if (f.default !== undefined && f.default !== null) defaults[f.name] = String(f.default)
      }
      setConfig(defaults)
      setSyncPayroll(provider?.supports_payroll_sync ?? false)
      setEnableWriteback(provider?.supports_writeback ?? false)
    }

    const collectConfig = (): Record<string, unknown> => {
      const schema = selectedProvider?.connection_config_schema ?? []
      const out: Record<string, unknown> = {}
      for (const f of schema) {
        const raw = config[f.name]
        if (f.type === 'integer' || f.type === 'number') {
          out[f.name] = raw !== undefined && raw !== '' ? Number(raw) : undefined
        } else if (f.type === 'boolean') {
          out[f.name] = raw === 'true'
        } else {
          out[f.name] = raw ?? ''
        }
      }
      return out
    }

    const handleTest = async () => {
      if (!selectedProvider) return
      setTesting(true)
      try {
        await testConnectionApi(selectedProvider.provider_id, collectConfig())
        toast.success('Connection test succeeded.')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Connection test failed')
      } finally {
        setTesting(false)
      }
    }

    const handleConnect = async () => {
      if (!selectedProvider) return
      setConnecting(true)
      try {
        await createOrgConnectionApi({
          provider_id: selectedProvider.provider_id,
          config: collectConfig(),
          sync_employees: syncEmployees,
          sync_payroll: syncPayroll,
          enable_writeback: enableWriteback,
        })
        toast.success(`Connected to ${selectedProvider.display_name}.`)
        setShowConnect(false)
        setSelectedProviderId('')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to connect provider')
      } finally {
        setConnecting(false)
      }
    }

    const handleDelete = async (id: number) => {
      try {
        await deleteOrgConnectionApi(id)
        toast.success('Connection removed.')
        setConfirmDelete(null)
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to remove connection')
      }
    }

    const handleSync = async (id: number) => {
      setSyncingId(id)
      try {
        const result = await triggerSyncApi(id)
        toast.success('Sync triggered. Run may take a moment.')
        void result
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to trigger sync')
      } finally {
        setSyncingId(null)
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <Header />
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading integrations…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <Header onConnect={() => setShowConnect(true)} />

        {connections.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <PlugIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No payroll integrations connected</p>
            <p className="max-w-sm text-center text-xs text-muted-foreground">
              Connect your HRIS or payroll provider to import employees and push deductions automatically.
            </p>
            <Button size="sm" onClick={() => setShowConnect(true)} className="mt-1 gap-1.5">
              <PlusIcon className="size-4" />
              Connect provider
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {connections.map((conn) => (
              <div key={conn.connection_id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <PlugIcon className="size-4 shrink-0 text-primary" />
                      <span className="font-semibold">{conn.provider_name ?? conn.provider_code}</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(conn.status)}`}>
                        {conn.status}
                      </span>
                      {conn.label && <span className="text-xs text-muted-foreground">· {conn.label}</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        Last sync:{' '}
                        {conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : 'never'}
                      </span>
                      {conn.last_sync_status && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(conn.last_sync_status)}`}>
                          {conn.last_sync_status}
                        </span>
                      )}
                      {conn.enable_writeback && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">write-back</span>}
                    </div>
                    {conn.last_sync_error && (
                      <p className="mt-1 truncate text-xs text-red-500">{conn.last_sync_error}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleSync(conn.connection_id)} disabled={syncingId === conn.connection_id} className="size-8 p-0" title="Sync now">
                      {syncingId === conn.connection_id ? <LoaderIcon className="size-4 animate-spin" /> : <RefreshCwIcon className="size-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setLogsConnectionId(conn.connection_id)} className="size-8 p-0" title="Sync history">
                      <FileTextIcon className="size-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setReviewConnectionId(conn.connection_id)} className="relative size-8 p-0" title="Review import changes">
                      <ShieldAlertIcon className="size-4" />
                      {conn.pending_changes_count ? (
                        <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-semibold text-white">
                          {conn.pending_changes_count > 9 ? '9+' : conn.pending_changes_count}
                        </span>
                      ) : null}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(conn.connection_id)} className="size-8 p-0 text-destructive" title="Delete">
                      <Trash2Icon className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Connect provider dialog ── */}
        <Dialog
          open={showConnect}
          onClose={() => { setShowConnect(false); setSelectedProviderId('') }}
          title="Connect payroll provider"
          description="Select a provider and enter its connection details."
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setShowConnect(false)}>Cancel</Button>
              <Button size="sm" variant="outline" onClick={handleTest} disabled={testing || !selectedProvider} className="gap-1.5">
                {testing ? <LoaderIcon className="size-4 animate-spin" /> : <RefreshCwIcon className="size-4" />}
                Test
              </Button>
              <Button size="sm" onClick={handleConnect} disabled={connecting || !selectedProvider} className="gap-1.5">
                {connecting ? <LoaderIcon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
                Connect
              </Button>
            </>
          }
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Provider *</label>
              <select
                value={selectedProviderId}
                onChange={(e) => selectProvider(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">Select a provider…</option>
                {providers.map((p) => (
                  <option key={p.provider_id} value={String(p.provider_id)}>{p.display_name}</option>
                ))}
              </select>
            </div>

            {selectedProvider && selectedProvider.connection_config_schema.map((field) => {
              if (field.type === 'boolean') {
                return (
                  <label key={field.name} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config[field.name] === 'true'}
                      onChange={(e) => setConfig((c) => ({ ...c, [field.name]: String(e.target.checked) }))}
                    />
                    {field.name}
                  </label>
                )
              }
              return (
                <div key={field.name}>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    {field.name}{field.required ? ' *' : ''}
                  </label>
                  <input
                    type={fieldInputType(field)}
                    value={config[field.name] ?? ''}
                    onChange={(e) => setConfig((c) => ({ ...c, [field.name]: e.target.value }))}
                    placeholder={field.name}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
                  />
                </div>
              )
            })}

            {selectedProvider && (
              <div className="space-y-2 rounded-lg bg-muted/50 p-3">
                <p className="text-xs font-medium text-muted-foreground">Sync options</p>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={syncEmployees} onChange={(e) => setSyncEmployees(e.target.checked)} />
                  Import employees
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={syncPayroll} onChange={(e) => setSyncPayroll(e.target.checked)} />
                  Import payroll runs
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={enableWriteback} onChange={(e) => setEnableWriteback(e.target.checked)} />
                  Deduction write-back
                </label>
              </div>
            )}
          </div>
        </Dialog>

        {logsConnectionId !== null && <SyncLogsDialog connectionId={logsConnectionId} onClose={() => setLogsConnectionId(null)} />}
        {reviewConnectionId !== null && <PendingChangesDialog connectionId={reviewConnectionId} onClose={() => setReviewConnectionId(null)} />}

        <DestructiveConfirm
          open={confirmDelete !== null}
          onClose={() => setConfirmDelete(null)}
          title="Remove connection?"
          description="This disconnects the provider and deletes its sync history for your organization."
          confirmLabel="Remove"
          onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        />
      </div>
    )
  },
})

function Header({ onConnect }: { onConnect?: () => void }) {
  return (
    <div className="mb-8">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
        <div className="flex flex-1 items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Payroll integrations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect your HRIS or payroll provider to import employees and push deductions.
            </p>
          </div>
          {onConnect && (
            <Button size="sm" onClick={onConnect} className="gap-1.5 shrink-0">
              <PlusIcon className="size-4" />
              Connect provider
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}